import React, { useMemo, useState } from 'react';
import { trim, uniq } from 'lodash';
import { Check } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from 'shadcn/components/ui/DropdownMenu';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';

import { FilterChip } from './FilterChip.jsx';

function TextFilter({ id, label, onChange, placeholder, value }) {
    return (
        <div className="flex flex-col gap-1">
            {label && <Label htmlFor={id}>{label}</Label>}
            <Input id={id} placeholder={placeholder} value={value || ''} onChange={(e) => onChange(e.target.value)} />
        </div>
    );
}

export { TextFilter };

// Menu variant: wraps the input inside a submenu panel
function TextFilterMenuItem({ id, label, onChange, placeholder, value }) {
    const [draft, setDraft] = useState('');
    const list = useMemo(() => (Array.isArray(value) ? value : value ? [value] : []), [value]);

    const addDraft = () => {
        const nextVal = trim(draft || '');
        if (!nextVal) return;
        const next = uniq([...list, nextVal]);
        onChange(next);
        setDraft('');
    };

    return (
        <DropdownMenuSub>
            <DropdownMenuSubTrigger inset>{label}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="p-0">
                <div className="px-3 py-3 w-[360px]">
                    <div className="flex flex-col gap-1">
                        {label && <Label htmlFor={id}>{label}</Label>}
                        <div className="flex items-center gap-2">
                            <Input
                                id={id}
                                placeholder={placeholder}
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addDraft();
                                    }
                                }}
                                className="h-8 px-2 text-sm"
                            />
                            <Button size="iconSm" variant="secondary" aria-label="Queue value" onClick={addDraft}>
                                <Check />
                            </Button>
                        </div>
                        {list && list.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                                {list.map((v, idx) => (
                                    <FilterChip
                                        key={`${id}-text-${idx}`}
                                        label={String(v)}
                                        deletable
                                        onDelete={() => {
                                            const next = list.filter((x) => x !== v);
                                            onChange(next.length > 0 ? next : undefined);
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </DropdownMenuSubContent>
        </DropdownMenuSub>
    );
}

export { TextFilterMenuItem };
